import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Users,
  UserPlus,
  TrendingUp,
  Clock,
  Target,
  CheckCircle2,
  XCircle,
  ArrowRight,
  Phone,
  Building2,
} from 'lucide-react';
import { getLeadStats, getLeads } from '../../lib/api';

const STATUS_COLORS = {
  new: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-yellow-100 text-yellow-700',
  negotiation: 'bg-purple-100 text-purple-700',
  contracting: 'bg-indigo-100 text-indigo-700',
  on_hold: 'bg-slate-100 text-slate-700',
  closed_won: 'bg-green-100 text-green-700',
  closed_lost: 'bg-red-100 text-red-700',
  postponed: 'bg-orange-100 text-orange-700',
};

export default function SalesDashboardPage() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsRes, leadsRes] = await Promise.all([
        getLeadStats(),
        getLeads({ limit: 5, sort_by: 'created_at', sort_order: 'desc' })
      ]);
      setStats(statsRes.data);
      setRecentLeads(Array.isArray(leadsRes?.data?.leads) ? leadsRes.data.leads : []);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <Card key={i}><CardContent className="p-6"><Skeleton className="h-20" /></CardContent></Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sales-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Sales Dashboard</h1>
          <p className="text-slate-500">Track and manage your leads pipeline</p>
        </div>
        <Button asChild className="bg-indigo-600 hover:bg-indigo-700">
          <Link to="/sales/leads/new">
            <UserPlus className="w-4 h-4 mr-2" />
            Add New Lead
          </Link>
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-blue-100">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Leads</p>
                <p className="text-2xl font-bold text-slate-900">{stats?.total || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-green-100">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Converted</p>
                <p className="text-2xl font-bold text-slate-900">{stats?.converted || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-indigo-100">
                <TrendingUp className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Conversion Rate</p>
                <p className="text-2xl font-bold text-slate-900">{stats?.conversion_rate || 0}%</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-amber-100">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">This Week</p>
                <p className="text-2xl font-bold text-slate-900">{stats?.recent_week || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pipeline Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-indigo-600" />
              Pipeline by Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(stats?.by_status || {}).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge className={STATUS_COLORS[status]}>
                      {status.replace('_', ' ')}
                    </Badge>
                  </div>
                  <span className="font-semibold text-slate-900">{count}</span>
                </div>
              ))}
              {Object.keys(stats?.by_status || {}).length === 0 && (
                <p className="text-slate-500 text-center py-4">No leads yet</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Source Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-indigo-600" />
              Leads by Source
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(stats?.by_source || {}).map(([source, count]) => (
                <div key={source} className="flex items-center justify-between">
                  <span className="text-slate-600 capitalize">{source.replace('_', ' ')}</span>
                  <span className="font-semibold text-slate-900">{count}</span>
                </div>
              ))}
              {Object.keys(stats?.by_source || {}).length === 0 && (
                <p className="text-slate-500 text-center py-4">No leads yet</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Leads */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Recent Leads</CardTitle>
          <Button variant="outline" size="sm" asChild>
            <Link to="/sales/leads">
              View All
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </Button>
        </CardHeader>
        <CardContent>
          {recentLeads.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No leads yet. Add your first lead to get started!</p>
              <Button asChild className="mt-4 bg-indigo-600 hover:bg-indigo-700">
                <Link to="/sales/leads/new">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Lead
                </Link>
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {recentLeads.map((lead) => (
                <Link
                  key={lead.lead_id}
                  to={`/sales/leads/${lead.lead_id}`}
                  className="block p-4 rounded-lg border border-slate-200 hover:border-indigo-300 hover:shadow-sm transition-all"
                  data-testid={`lead-card-${lead.lead_id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900">{lead.business_name}</h4>
                        {lead.contact_name && (
                          <p className="text-sm text-slate-500">{lead.contact_name}</p>
                        )}
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs text-slate-400 flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {lead.phone}
                          </span>
                          <span className="text-xs text-slate-400 capitalize">
                            {lead.business_type?.replace('_', ' ')}
                          </span>
                        </div>
                      </div>
                    </div>
                    <Badge className={STATUS_COLORS[lead.status]}>
                      {lead.status.replace('_', ' ')}
                    </Badge>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
